import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { contarPendentes, drenarFila, puxarTudo, requeuePermissoes } from '../sync/engine';
import { useAuth } from './AuthContext';

const SyncContext = createContext(null);

export const SyncProvider = ({ children }) => {
  const { fazenda } = useAuth();
  const [online, setOnline] = useState(navigator.onLine);
  const [pendentes, setPendentes] = useState(0);
  const [sincronizando, setSincronizando] = useState(false);
  const [ultimoErro, setUltimoErro] = useState(null);
  const emAndamento = useRef(false);
  const requeueFeitaNaSessao = useRef(false);

  const atualizarPendentes = useCallback(async () => {
    setPendentes(await contarPendentes());
  }, []);

  const sincronizar = useCallback(async () => {
    if (emAndamento.current || !navigator.onLine || !fazenda) return;
    emAndamento.current = true;
    setSincronizando(true);
    setUltimoErro(null);
    try {
      await drenarFila({ onEntrada: () => atualizarPendentes() });
      await puxarTudo();
    } catch (erro) {
      setUltimoErro(String(erro));
    } finally {
      await atualizarPendentes();
      setSincronizando(false);
      emAndamento.current = false;
    }
  }, [fazenda, atualizarPendentes]);

  useEffect(() => {
    const aoConectar = () => {
      setOnline(true);
      sincronizar();
    };
    const aoDesconectar = () => setOnline(false);

    window.addEventListener('online', aoConectar);
    window.addEventListener('offline', aoDesconectar);
    return () => {
      window.removeEventListener('online', aoConectar);
      window.removeEventListener('offline', aoDesconectar);
    };
  }, [sincronizar]);

  useEffect(() => {
    atualizarPendentes();
  }, [atualizarPendentes]);

  useEffect(() => {
    if (fazenda && navigator.onLine) {
      if (!requeueFeitaNaSessao.current) {
        requeueFeitaNaSessao.current = true;
        requeuePermissoes().then(() => sincronizar());
      } else {
        sincronizar();
      }
    }
  }, [fazenda, sincronizar]);

  return (
    <SyncContext.Provider value={{ online, pendentes, sincronizando, ultimoErro, sincronizar }}>
      {children}
    </SyncContext.Provider>
  );
};

export const useSync = () => useContext(SyncContext);
